import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';

const STORAGE_KEY = 'orbcon_user_sidebar_open';
const DEFAULT_OPEN_QUERY = '(min-width: 1024px)';
const MD_MIN_PX = 768;
const MAX_W_7XL_PX = 1280;
const FULL_NAV_MIN_PX = 900;
export const MY_ORBCON_LABEL = 'My ORBCoN';
export const MY_ORBCON_PANEL_ID = 'my-orbcon-panel';
export const MY_ORBCON_PANEL_WIDTH_PX = 320;

interface UserSidebarContextValue {
  isSignedIn: boolean;
  ready: boolean;
  isOpen: boolean;
  isDocked: boolean;
  panelPaddingPx: number;
  compactChrome: boolean;
  compactNav: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const UserSidebarContext = createContext<UserSidebarContextValue>({
  isSignedIn: false,
  ready: false,
  isOpen: false,
  isDocked: false,
  panelPaddingPx: 0,
  compactChrome: false,
  compactNav: false,
  setOpen: () => {},
  toggle: () => {},
});

function readViewportWidth() {
  if (typeof window === 'undefined') return 1280;
  return window.innerWidth;
}

function readStoredPreference(): boolean | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    // sessionStorage unavailable
  }
  return null;
}

function writeStoredPreference(open: boolean) {
  try {
    sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0');
  } catch {
    // sessionStorage unavailable
  }
}

export function UserSidebarProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(readViewportWidth);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setIsOpen(false);
      setReady(true);
      return;
    }

    const stored = readStoredPreference();
    if (stored !== null) {
      setIsOpen(stored);
    } else {
      setIsOpen(window.matchMedia(DEFAULT_OPEN_QUERY).matches);
    }
    setReady(true);
  }, [isLoaded, isSignedIn]);

  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    writeStoredPreference(open);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((current) => {
      const next = !current;
      writeStoredPreference(next);
      return next;
    });
  }, []);

  const signedInOpen = Boolean(isSignedIn) && isOpen;
  const sideGutter = Math.max(0, (viewportWidth - MAX_W_7XL_PX) / 2);
  const panelsPx = signedInOpen && viewportWidth >= MD_MIN_PX ? MY_ORBCON_PANEL_WIDTH_PX : 0;
  const panelPaddingPx = sideGutter >= panelsPx ? 0 : panelsPx;
  const isDocked = panelPaddingPx > 0;
  const availableWidth = viewportWidth - panelPaddingPx;
  const compactChrome = availableWidth < MD_MIN_PX;
  const compactNav = compactChrome || availableWidth < FULL_NAV_MIN_PX;

  const value = useMemo(
    () => ({
      isSignedIn: Boolean(isSignedIn),
      ready,
      isOpen: signedInOpen,
      isDocked,
      panelPaddingPx,
      compactChrome,
      compactNav,
      setOpen,
      toggle,
    }),
    [isSignedIn, ready, signedInOpen, isDocked, panelPaddingPx, compactChrome, compactNav, setOpen, toggle]
  );

  return createElement(UserSidebarContext.Provider, { value }, children);
}

export function useUserSidebar() {
  return useContext(UserSidebarContext);
}
